import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { z } from 'zod';
import {
  faArrowUpRightFromSquare,
  faBroomWide,
  faEllipsisVertical,
  faGear,
  faShare,
} from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';

import { ChatSettings, MessageType } from '@hooks';

import {
  Button,
  Field,
  Icon,
  Modal,
  ModalRef,
  Popover,
  Render,
} from '@components';

const settingsSchema = z.object({
  maxIterations: z.number().min(1).max(5),
  mode: z.enum(['hybrid', 'global', 'local', 'mix']),
});

type SettingsSchema = z.infer<typeof settingsSchema>;

const modeOptions = [
  { text: 'Hybrid', value: 'hybrid' },
  { text: 'Global', value: 'global' },
  { text: 'Local', value: 'local' },
  { text: 'Mix', value: 'mix' },
];

const maxIterationsOptions = Array.from({ length: 5 }, (_, i) => ({
  text: (i + 1).toString(),
  value: i + 1,
}));

type Props = {
  messages: MessageType[];
  isFullscreen: boolean;
  chatSettings: ChatSettings;
  isWaitingResponse: boolean;
  toggleFullscreen: () => void;
  onClearHistory: () => void;
  onSettingsChange: (settings: ChatSettings) => void;
};

export const ChatOptions = ({
  messages,
  isFullscreen,
  chatSettings,
  isWaitingResponse,
  toggleFullscreen,
  onClearHistory,
  onSettingsChange,
}: Props) => {
  const settingsModalRef = useRef<ModalRef>(null);

  const { register, handleSubmit } = useForm<SettingsSchema>({
    resolver: zodResolver(settingsSchema),
    defaultValues: chatSettings,
  });

  const [popoverOpen, setPopoverOpen] = useState(false);

  const hidePopover = () => setPopoverOpen(false);

  const handleDataSharingClick = () => {
    if (messages.length === 0) {
      return;
    }

    const formattedDate = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const description = `# Apolo Copilot Chat

> Messages: ${messages.length}  
> Mode: ${chatSettings.mode || 'N/A'}  
> Max Iterations: ${chatSettings.maxIterations || 'N/A'}  
> Exported: ${formattedDate}  

---\n\n`;

    const formatted = messages
      .map((message) => {
        const role = message.role === 'user' ? 'User' : 'Assistant';
        return `## ${role}\n${message.content}\n`;
      })
      .join('\n---\n\n');

    const blob = new Blob([`${description}${formatted}`], {
      type: 'text/plain;charset=utf-8',
    });

    const filename = `apolo-chat ${formattedDate}.md`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  };

  const handleSettingsSubmit = handleSubmit(({ maxIterations, mode }) => {
    onSettingsChange({ maxIterations, mode });
    settingsModalRef.current?.close();
  });

  const options = [
    {
      title: 'Full page',
      icon: faArrowUpRightFromSquare,
      hotKey: 'Ctrl/Cmd + Shift + F',
      isActive: isFullscreen,
      onClick: toggleFullscreen,
    },
    {
      title: 'Copilot settings',
      icon: faGear,
      onClick: () => {
        settingsModalRef.current?.open();
      },
    },
    {
      title: 'Data sharing',
      icon: faShare,
      disabled: messages.length === 0,
      onClick: handleDataSharingClick,
    },
    {
      title: 'Clear chat',
      icon: faBroomWide,
      disabled: messages.length === 0 || isWaitingResponse,
      onClick: onClearHistory,
    },
  ];

  return (
    <>
      <Popover
        open={popoverOpen}
        showArrow={false}
        side="bottom"
        align="end"
        className="z-[31] w-[200px] p-0"
        onOpenChange={setPopoverOpen}
      >
        <div
          slot="trigger"
          className={clsx(
            'flex h-12 w-12 items-center justify-center rounded-lg text-black transition-colors',
            {
              'bg-gray-200': popoverOpen,
              'hover:bg-gray-100': !popoverOpen,
            },
          )}
        >
          <Button
            theme
            aria-label="Chat options"
            onClick={() => setPopoverOpen(!popoverOpen)}
          >
            <Icon icon={faEllipsisVertical} size="xl" />
          </Button>
        </div>

        <div className="w-full py-2">
          {options.map(
            ({ title, icon, disabled, hotKey, isActive, onClick }) => (
              <Button
                key={title}
                theme
                disabled={disabled}
                className={clsx(
                  'relative flex w-full items-center gap-3 px-4 py-[10px] text-black transition-all',
                  {
                    'cursor-not-allowed opacity-50': disabled,
                    'group hover:bg-neural-01': !disabled,
                    'bg-neural-01/60': isActive,
                    '-mb-1': hotKey,
                  },
                )}
                onClick={() => {
                  hidePopover();
                  onClick();
                }}
              >
                <Icon
                  icon={icon}
                  className="text-neural-04 transition-colors group-hover:text-black"
                />
                <span className="flex flex-col items-start">
                  {title}
                  <Render if={hotKey}>
                    <span className="text-[11px] leading-[11px] text-neural-03">
                      {hotKey}
                    </span>
                  </Render>
                </span>
              </Button>
            ),
          )}
        </div>
      </Popover>

      <Modal
        ref={settingsModalRef}
        content={
          <Modal.Content title="Copilot Settings" className="w-[480px]">
            <form className="space-y-4" onSubmit={handleSettingsSubmit}>
              <Field.Select
                {...register('mode')}
                label="Mode"
                note="Select the reasoning mode for the copilot"
                options={modeOptions}
              />

              <Field.Select
                {...register('maxIterations', { valueAsNumber: true })}
                label="Max iterations"
                note="Number of reasoning iterations the copilot can perform"
                options={maxIterationsOptions}
              />
            </form>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => settingsModalRef.current?.close()}
              >
                Cancel
              </Button>

              <Button
                className="px-10 capitalize"
                onClick={handleSettingsSubmit}
              >
                Save
              </Button>
            </Modal.Footer>
          </Modal.Content>
        }
      />
    </>
  );
};
